import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import {
  paymentDemandCreateSchema,
  VAT_RATE_OPTIONS,
  vatRateLabel,
  type PaymentDemandCreateInput,
  type PaymentDemandDto,
  type PaymentDemandStatus,
} from '@expeditor/shared';
import { paymentDemandsApi } from '../../shared/api/payment-demands';

const STATUS_COLOR: Record<PaymentDemandStatus, string> = {
  DRAFT: 'gray',
  ISSUED: 'blue',
  PAID: 'green',
  CANCELLED: 'gray',
};
const STATUS_LABEL: Record<PaymentDemandStatus, string> = {
  DRAFT: 'Черновик',
  ISSUED: 'Выставлено',
  PAID: 'Оплачено',
  CANCELLED: 'Отменено',
};

interface LineForm {
  name: string;
  amountNet: number | string;
  vatRate: string;
}
const fmt = (n: number) =>
  n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  shipmentId: string;
  /** Менеджер/админ — может создавать и менять статусы. */
  canManage: boolean;
}

/**
 * Требования оплаты (доп. расходы) по перевозке.
 *
 * Менеджер фиксирует доп. расходы позициями (своя ставка НДС у каждой) и
 * выставляет клиенту; клиент видит сумму к доплате.
 */
export function PaymentDemandsSection({ shipmentId, canManage }: Props) {
  const [items, setItems] = useState<PaymentDemandDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const [lines, setLines] = useState<LineForm[]>([{ name: '', amountNet: '', vatRate: '20' }]);
  const [currency, setCurrency] = useState('RUB');
  const [comment, setComment] = useState('');
  const [issueNow, setIssueNow] = useState(true);

  const totalNet = lines.reduce((s, l) => s + (Number(l.amountNet) || 0), 0);
  const totalVat = lines.reduce(
    (s, l) => s + ((Number(l.amountNet) || 0) * (Number(l.vatRate) || 0)) / 100,
    0,
  );
  const lineUpdate = (i: number, patch: Partial<LineForm>) =>
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const lineRemove = (i: number) => setLines(lines.filter((_, idx) => idx !== i));
  const addBlankLine = () => setLines([...lines, { name: '', amountNet: '', vatRate: '20' }]);

  const reload = async () => {
    try {
      setItems(await paymentDemandsApi.listForShipment(shipmentId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить требования оплаты');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  const openCreate = () => {
    setLines([{ name: '', amountNet: '', vatRate: '20' }]);
    setComment('');
    setIssueNow(true);
    setCreating(true);
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validLines = lines.filter((l) => l.name.trim() && l.amountNet !== '');
    if (validLines.length === 0) {
      setError('Добавьте хотя бы один расход с суммой');
      return;
    }
    const dto: PaymentDemandCreateInput = {
      currency: currency.toUpperCase(),
      lines: validLines.map((l) => ({
        name: l.name.trim(),
        amountNet: Number(l.amountNet),
        vatRate: Number(l.vatRate || '0'),
      })),
      comment: comment || undefined,
      issueNow,
    };
    const parsed = paymentDemandCreateSchema.safeParse(dto);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      return;
    }
    setBusy(true);
    try {
      await paymentDemandsApi.createForShipment(shipmentId, parsed.data);
      setCreating(false);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось создать требование');
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<unknown>, confirmText?: string) => {
    if (confirmText && !confirm(confirmText)) return;
    setBusy(true);
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось выполнить действие');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <div>
          <Title order={4}>Требования оплаты</Title>
          <Text size="xs" c="dimmed">
            Доп. расходы, возникшие в ходе перевозки
          </Text>
        </div>
        {canManage && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate} size="xs">
            Добавить требование
          </Button>
        )}
      </Group>

      {error && (
        <Alert color="red" mb="sm" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {items.length === 0 ? (
        <Text c="dimmed" size="sm">
          Требований оплаты пока нет.
        </Text>
      ) : (
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Номер</Table.Th>
              <Table.Th>Сумма</Table.Th>
              <Table.Th>Расходы</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th style={{ width: 180 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>
                  <Text size="sm">{d.number}</Text>
                  {d.comment && (
                    <Text size="xs" c="dimmed">
                      {d.comment}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text fw={500}>
                    {fmt(d.amount)} {d.currency}
                  </Text>
                  <Text size="xs" c="dimmed">
                    без НДС {fmt(d.amountNet)} · НДС {fmt(d.vatAmount)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Stack gap={0}>
                    {d.lines.map((l, i) => (
                      <Text key={i} size="xs">
                        {l.name} — {fmt(l.amountNet)} ({vatRateLabel(l.vatRate)})
                      </Text>
                    ))}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLOR[d.status]}>{STATUS_LABEL[d.status]}</Badge>
                </Table.Td>
                <Table.Td>
                  {canManage && (
                    <Group gap={4}>
                      {d.status === 'DRAFT' && (
                        <Button
                          size="xs"
                          onClick={() => act(() => paymentDemandsApi.issue(d.id))}
                          loading={busy}
                        >
                          Выставить
                        </Button>
                      )}
                      {d.status === 'ISSUED' && (
                        <Button
                          size="xs"
                          color="green"
                          onClick={() =>
                            act(() => paymentDemandsApi.pay(d.id), 'Отметить требование оплаченным?')
                          }
                          loading={busy}
                        >
                          Оплачено
                        </Button>
                      )}
                      {(d.status === 'DRAFT' || d.status === 'ISSUED') && (
                        <Button
                          size="xs"
                          variant="default"
                          onClick={() =>
                            act(() => paymentDemandsApi.cancel(d.id), 'Отменить требование?')
                          }
                          loading={busy}
                        >
                          Отменить
                        </Button>
                      )}
                    </Group>
                  )}
                  {!canManage && d.status === 'ISSUED' && (
                    <Text size="sm" c="blue" fw={500}>
                      К оплате
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal
        opened={creating}
        onClose={() => setCreating(false)}
        title="Новое требование оплаты"
        size="lg"
      >
        <form onSubmit={onCreate}>
          <Stack>
            <Text size="sm" fw={500}>
              Доп. расходы
            </Text>
            {lines.map((l, i) => (
              <Group key={i} align="flex-end" gap="xs" wrap="nowrap">
                <TextInput
                  label={i === 0 ? 'Расход' : undefined}
                  value={l.name}
                  onChange={(e) => lineUpdate(i, { name: e.currentTarget.value })}
                  style={{ flex: 1 }}
                  placeholder="Простой, перевес, хранение..."
                />
                <NumberInput
                  label={i === 0 ? 'Сумма без НДС' : undefined}
                  value={l.amountNet}
                  onChange={(v) => lineUpdate(i, { amountNet: v })}
                  min={0}
                  decimalScale={2}
                  thousandSeparator=" "
                  w={150}
                />
                <Select
                  label={i === 0 ? 'НДС' : undefined}
                  data={VAT_RATE_OPTIONS}
                  value={l.vatRate}
                  onChange={(v) => lineUpdate(i, { vatRate: v ?? '0' })}
                  w={100}
                  allowDeselect={false}
                />
                <ActionIcon
                  color="red"
                  variant="subtle"
                  mb={6}
                  onClick={() => lineRemove(i)}
                  disabled={lines.length === 1}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Group>
              <Button
                variant="default"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={addBlankLine}
              >
                Добавить расход
              </Button>
            </Group>
            <Group justify="flex-end" gap="lg">
              <Text size="sm" c="dimmed">
                Без НДС: {fmt(totalNet)}
              </Text>
              <Text size="sm" c="dimmed">
                НДС: {fmt(totalVat)}
              </Text>
              <Text fw={600}>
                Итого: {fmt(totalNet + totalVat)} {currency}
              </Text>
            </Group>
            <Group grow>
              <TextInput
                label="Валюта (ISO 4217)"
                required
                value={currency}
                onChange={(e) => setCurrency(e.currentTarget.value)}
                maxLength={3}
              />
            </Group>
            <Textarea
              label="Комментарий (необязательно)"
              minRows={2}
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              placeholder="Основание доп. расходов, ссылка на документы..."
            />
            <Checkbox
              label="Сразу выставить клиенту"
              checked={issueNow}
              onChange={(e) => setIssueNow(e.currentTarget.checked)}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreating(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={busy}>
                {issueNow ? 'Создать и выставить' : 'Сохранить черновик'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Card>
  );
}
